import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress, Button, Container, Alert, TextField, MenuItem, Stack, FormControl, InputLabel, Select } from '@mui/material';
import { useTranslation } from 'react-i18next';
import disputeService from '../services/disputeService';
import DisputeListCard from '../components/disputes/DisputeListCard';
import DateRangePicker from '../components/common/DateRangePicker';
import '../components/financial/PaymentHistoryInfinite.css';

const AllDisputesPage = () => {
  const { t } = useTranslation(['disputes', 'common']);

  const [disputes, setDisputes] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(5);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', status: '', disputeId: '', listingTitle: '' });
  const filtersRef = useRef(filters);

  // Mirror of disputes state — lets us compute deduplication synchronously
  const disputesRef = useRef([]);

  // Generation counter: each new fetch increments it.
  // Any response that doesn't match the current generation is stale and discarded.
  // This prevents React 18 StrictMode's double-invocation of useEffect from
  // having the 2nd call see the ref already populated by the 1st call (→ newItems:0 → hasMore:false).
  const fetchGenRef = useRef(0);

  const normalizeResponse = (response) => {
    console.log('[AllDisputesPage] normalizeResponse — raw API response:', JSON.stringify(response, null, 2));

    if (!response) return { items: [], total: 0, hasNext: null };
    if (Array.isArray(response)) return { items: response, total: response.length, hasNext: null };

    const hasNext = typeof response?.pagination?.hasNext === 'boolean'
      ? response.pagination.hasNext
      : null;

    if (Array.isArray(response.data)) {
      const rawTotal = response.total ?? response.pagination?.total ?? response.pagination?.total_items ?? 0;
      let total = Number(rawTotal);
      if (!Number.isFinite(total)) total = 0;

      // Ensure total is at least items.length to reflect actual capacity
      total = Math.max(total, response.data.length);
      console.log('[AllDisputesPage] normalizeResponse — parsed:', { total, hasNext, itemsCount: response.data.length });
      return { items: response.data, total, hasNext };
    }

    if (Array.isArray(response.results)) {
      const rawTotal = response.total ?? response.results.length;
      let total = Number(rawTotal);
      if (!Number.isFinite(total)) total = response.results.length;
      total = Math.max(total, response.results.length);
      console.log('[AllDisputesPage] normalizeResponse — parsed (results):', { total, hasNext, itemsCount: response.results.length });
      return { items: response.results, total, hasNext };
    }

    console.warn('[AllDisputesPage] normalizeResponse — unrecognised shape, returning empty');
    return { items: [], total: 0, hasNext };
  };
  const buildParams = (p, providedFilters = {}) => {
    const params = { page: p, limit };

    // Map simple filter fields
    if (providedFilters.status) params.status = providedFilters.status;
    if (providedFilters.disputeId) params.dispute_id = providedFilters.disputeId;
    if (providedFilters.listingTitle) params.listing_title = providedFilters.listingTitle;

    // Map explicit start/end date filters
    // DateRangePicker returns dates, if they're Date objects convert to YYYY-MM-DD
    const formatDate = (d) => {
      if (!d) return null;
      if (typeof d === 'string') return d;
      if (d instanceof Date) return d.toISOString().split('T')[0];
      return d;
    };
    const formattedStart = formatDate(providedFilters.startDate);
    const formattedEnd = formatDate(providedFilters.endDate);
    if (formattedStart) params.start_date = formattedStart;
    if (formattedEnd) params.end_date = formattedEnd;

    return params;
  };

  const fetchPage = useCallback(async (p, providedFilters = null) => {
    const myGen = ++fetchGenRef.current;
    setLoading(true);
    setError(null);
    try {
      // Call new endpoint for user disputes with filters
      const effectiveFilters = providedFilters || filtersRef.current || {};
      const params = buildParams(p, effectiveFilters);
      const response = await disputeService.listUser(params);

      // Discard stale responses (e.g. React 18 StrictMode double-invoke)
      if (myGen !== fetchGenRef.current) return;

      const { items, total, hasNext } = normalizeResponse(response);
      setTotalItems(total);

      console.log(`[AllDisputesPage] fetchPage(${p}) gen=${myGen} — items:${items.length} total:${total} hasNext:${hasNext}`);

      // Deduplicate SYNCHRONOUSLY using the ref (not inside setDisputes closure)
      const existingIds = new Set(
        disputesRef.current.map(d => d.id || d._id || d.dispute_id || d.disputeId)
      );
      const newItems = items.filter(it => {
        const id = it.id || it._id || it.dispute_id || it.disputeId;
        return !existingIds.has(id);
      });
      const nextDisputes = [...disputesRef.current, ...newItems];

      // Update the ref and state together
      disputesRef.current = nextDisputes;
      setDisputes(nextDisputes);

      const computedHasMore = hasNext !== null ? hasNext : nextDisputes.length < total;
      console.log(`[AllDisputesPage] setHasMore — newItems:${newItems.length} nextDisputes:${nextDisputes.length} total:${total} hasNext:${hasNext} → hasMore:${newItems.length === 0 ? false : computedHasMore}`);

      if (newItems.length === 0) {
        setHasMore(false);
        return;
      }

      setHasMore(computedHasMore);
    } catch (err) {
      if (myGen !== fetchGenRef.current) return;
      console.error('Failed to load disputes:', err);
      setError(t('list.error', 'Erreur lors du chargement des litiges'));
      setHasMore(false);
    } finally {
      if (myGen === fetchGenRef.current) {
        setLoading(false);
        if (p === 1) setInitialLoading(false);
      }
    }
  }, [limit, t]);


  // Keep filtersRef up to date
  useEffect(() => { filtersRef.current = filters; }, [filters]);

  // Auto-trigger fetch when filters change — debounced 400ms for text fields
  useEffect(() => {
    const debounceMs = 400;
    const timer = setTimeout(() => {
      setDisputes([]);
      disputesRef.current = [];
      setTotalItems(0);
      setPage(1);
      setInitialLoading(true);
      fetchPage(1, filters);
    }, debounceMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    if (page === 1) return;
    if (loading) return;
    fetchPage(page);
  }, [page, fetchPage]);

  const handleLoadMore = () => {
    if (loading) return;
    if (!hasMore) return;
    setPage(p => p + 1);
  };

  const handleClearFilters = () => {
    const empty = { startDate: '', endDate: '', status: '', disputeId: '', listingTitle: '' };
    setFilters(empty);
  };

  const openDisputeUrl = (dispute) => {
    const id = dispute?.id || dispute?._id || dispute?.dispute_id || dispute?.disputeId;
    window.open(`/disputes/${id}`, '_blank');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3, width: '75%', mx: 'auto' }}>
      {/* En-tête */}
      <Box sx={{ mb: 4 }}>
        <Box className="payment-history-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 0 }}>
              {t('disputes:all.title', 'Mes litiges')}
            </Typography>
            <Typography variant="body1" color="white" sx={{ mt: 1, mb: 2 }}>
              {t('disputes:all.subtitle', 'Suivez vos litiges, leur état et l’historique des échanges.')}
            </Typography>
          </Box>
          {disputes.length > 0 && (
             <div className="history-count">                      
              <Typography variant="body2" sx={{ color: 'inherit' }}>
                {t('disputes:all.count', { count: disputes.length, total: totalItems || '?' }, `${disputes.length} litige(s) chargé(s) sur ${totalItems || '?'}`)}
              </Typography>            
            </div>
          )}
        </Box>
        {disputes.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('disputes:all.noDisputes', "Vous n'avez aucun litige")}
          </Typography>
        )}
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
          <Box sx={{ minWidth: 300 }}>
            <DateRangePicker
              startDate={filters.startDate}
              endDate={filters.endDate}
              onDateRangeChange={(start, end) => setFilters(f => ({ ...f, startDate: start, endDate: end }))}
              label={t('disputes:filters.dateRange', 'Plage de dates')}
              size="small"
            />
          </Box>
          <TextField
            label={t('disputes:filters.disputeId', 'ID litige')}
            value={filters.disputeId}
            onChange={(e) => setFilters(f => ({ ...f, disputeId: e.target.value }))}
            size="small"
            sx={{ minWidth: 220 }}
          />
          <TextField
            label={t('disputes:filters.listingTitle', 'Titre du listing')}
            value={filters.listingTitle}
            onChange={(e) => setFilters(f => ({ ...f, listingTitle: e.target.value }))}
            size="small"
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="dispute-status-label">{t('disputes:filters.status', 'Statut')}</InputLabel>
            <Select
              labelId="dispute-status-label"
              label={t('disputes:filters.status', 'Statut')}
              value={filters.status}
              onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            >
              <MenuItem value="">{t('common:all', 'Tous')}</MenuItem>
              <MenuItem value="pending">{t('disputes:status.pending', 'En attente')}</MenuItem>
              <MenuItem value="in_review">{t('disputes:status.in_progress', 'En cours')}</MenuItem>
              <MenuItem value="resolved">{t('disputes:status.resolved', 'Résolu')}</MenuItem>
              <MenuItem value="escalated">{t('disputes:status.escalated', 'Escaladé')}</MenuItem>
              <MenuItem value="closed">{t('disputes:status.closed', 'Fermé')}</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={handleClearFilters} size="small" sx={{ textTransform: 'none' }}>{t('common:clear', 'Effacer')}</Button>
        </Stack>
      </Box>

      {/* Affichage initial loading */}
      {initialLoading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            {t('common:loading', 'Chargement...')}
          </Typography>
        </Box>
      )}

      {/* Erreur */}
      {error && !initialLoading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Liste des disputes */}
      {!initialLoading && (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {disputes.map((d) => (
          <DisputeListCard
            key={d.id || d._id || d.dispute_id || d.disputeId}
            dispute={d}
            onOpen={openDisputeUrl}
          />
        ))}
      </Box>
      )}

      {/* Charger plus */}
      {!initialLoading && disputes.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loading || !hasMore}
            sx={{ textTransform: 'none' }}
          >
            {loading ? t('common:loading', 'Chargement...') : t('common:loadMore', 'Charger plus')}
          </Button>
        </Box>
      )}

      {!initialLoading && loading && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {!hasMore && !initialLoading && !loading && disputes.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            ✅ {t('disputes:all.end', 'Tous les litiges ont été chargés')}
          </Typography>
        </Box>
      )}

      {!hasMore && !initialLoading && disputes.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary">
            {t('disputes:all.noDisputes', "Vous n'avez aucun litige")}
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default AllDisputesPage;
